import { useMemo, useState, type ComponentType } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { READ_ONLY_TITLE, useReadOnly, useStore, usePhase } from "@/state/store";
import { useSlideState } from "@/state/slide-context";
import { api } from "@/api/client";
import { ConfirmDialog } from "./ConfirmDialog";
import { LogoutButton } from "./LogoutButton";
import { ArSessionControl } from "./ArSessionControl";
import { useOvernightStatus } from "@/hooks/useOvernightStatus";
import type { Phase } from "@offsite/shared";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import type { PhaseId } from "@/engine/app/offsite-config";
import {
  IconAiBuild,
  IconAiEval,
  IconBackend,
  IconChevronLeft,
  IconChevronRight,
  IconGroupResults,
  IconHome,
  IconRequirement,
  IconSlides,
  IconTopics,
} from "./SvoIcons";

const STORAGE_KEY = "offsite:sidebar-collapsed";

interface WorkshopItem {
  label: string;
  /** Phase, die beim Klick gesetzt wird (kann von der Config-Phase abweichen). */
  phase: Phase;
  /** Other phases that should also light this item up as "active" */
  alsoActiveFor?: Phase[];
  durationSec?: number;
  hint?: string;
  Icon: ComponentType<{ size?: number }>;
}

/**
 * Darstellungs-Details je Config-Phase. Die Beschriftungen leben bewusst hier
 * und nicht in der OffsiteConfig — welche Punkte ein Offsite überhaupt zeigt,
 * entscheidet dagegen `config.phases`.
 *
 * Nicht jede Config-Phase hat einen eigenen Navigationspunkt:
 * - `intro` steckt in der Sektion "Folien",
 * - `voting`/`grouping` sind in "Themen" gefaltet (alsoActiveFor),
 * - `showcase` hat einen eigenen Link in der Sektion "Backend",
 * - `overnight` ist in "KI Umsetzung" gefaltet.
 */
const PHASE_ITEMS: Partial<Record<PhaseId, WorkshopItem>> = {
  topics: {
    label: "Themen",
    phase: "topics",
    alsoActiveFor: ["voting", "winner-reveal", "grouping"],
    hint: "4 Themen → Voting → Sieger → Teams",
    Icon: IconTopics,
  },
  working: {
    label: "Anforderung",
    phase: "working",
    hint: "Gruppen schreiben — Timer manuell starten",
    Icon: IconRequirement,
  },
  evaluating: {
    label: "Ergebnis der Gruppen",
    phase: "submitted",
    hint: "Eingereichte Anforderungen",
    Icon: IconGroupResults,
  },
  evaluated: {
    label: "KI Bewertung",
    phase: "evaluated",
    alsoActiveFor: ["evaluating"],
    hint: "Score-Bars + Verbesserungsvorschläge",
    Icon: IconAiEval,
  },
  reveal: {
    label: "KI Umsetzung",
    phase: "reveal",
    alsoActiveFor: ["overnight"],
    hint: "HTML/Markdown/Mermaid-Outputs",
    Icon: IconAiBuild,
  },
};

/**
 * Navigationspunkte in der Reihenfolge, die die Offsite-Config vorgibt.
 *
 * Solo-Offsites führen ihren Ablauf über die Moderationsleiste in der
 * Inhaltsfläche; dort werden diese Punkte gar nicht gerendert. Deshalb gibt es
 * hier keine Solo-Beschriftungen mehr.
 */
export function workshopItemsFor(phases: PhaseId[]): WorkshopItem[] {
  const items: WorkshopItem[] = [];
  for (const p of phases) {
    const item = PHASE_ITEMS[p];
    if (!item) continue;
    if (!items.some((i) => i.phase === item.phase)) items.push(item);
  }
  return items;
}

interface Props {
  /** Optional override; wenn nicht gesetzt, wird der globale SlideContext genutzt. */
  slideIndex?: number;
  slideTotal?: number;
  /** Wenn nicht gesetzt: navigate('/present') und reportIndex(i) im Context. */
  onJumpSlide?: (index: number) => void;
  /** Bei Cross-Route-Embed (z.B. /workshop/*) ist workshopActive true,
   *  ein Klick auf "SLIDES" muss zur Slide-Carousel zurück navigieren. */
  workshopActive?: boolean;
  forceSlides?: boolean;
  onToggleForceSlides?: (next: boolean) => void;
}

export function PresenterSidebar({
  slideIndex: slideIndexProp,
  slideTotal: slideTotalProp,
  onJumpSlide: onJumpSlideProp,
  workshopActive: workshopActiveProp,
  forceSlides: forceSlidesProp,
  onToggleForceSlides: onToggleForceSlidesProp,
}: Props = {}) {
  const navigate = useNavigate();
  const loc = useLocation();
  const slideCtx = useSlideState();

  const onPresent = loc.pathname === "/present";
  const slideIndex = slideIndexProp ?? slideCtx.index;
  const slideTotal = slideTotalProp ?? slideCtx.total;
  const onJumpSlide =
    onJumpSlideProp ??
    ((i: number) => {
      slideCtx.reportIndex(i);
      if (!onPresent) navigate(`/present?slide=${i + 1}`);
    });
  const onToggleForceSlides =
    onToggleForceSlidesProp ??
    ((next: boolean) => {
      if (next && !onPresent) navigate("/present");
    });
  // workshopActive wird auf /workshop/* manuell true, sonst false. Auf /present
  // konsumiert die alte PresenterPage diesen Flag noch direkt.
  const workshopActive =
    workshopActiveProp ?? loc.pathname.startsWith("/workshop/");
  const forceSlides = forceSlidesProp ?? false;
  const { state, dispatch } = useStore();
  const config = useOffsiteConfig();
  const workshopItems = useMemo(
    () => workshopItemsFor(config.phases),
    [config.phases],
  );
  const phase = usePhase();
  const readOnly = useReadOnly();
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [navErr, setNavErr] = useState<string | null>(null);
  const [jumpTo, setJumpTo] = useState(String(slideIndex + 1));

  // Poll overnight status while the lambda is in flight or once results are
  // ready, so the sidebar can show the live progress on "KI Umsetzung".
  const overnightStatus = useOvernightStatus(
    phase === "overnight" || phase === "reveal",
  );

  const toggle = () => {
    setCollapsed((c) => {
      const next = !c;
      try {
        window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* noop */
      }
      return next;
    });
  };

  const goToWorkshop = async (target: Phase, durationSec?: number) => {
    // For "KI Umsetzung" (target=reveal): if we're already in overnight or
    // reveal phase, don't flip the phase — just open the overlay. Setting
    // phase to "reveal" mid-overnight would jam the lambda's own state machine.
    if (target === "reveal" && (phase === "overnight" || phase === "reveal")) {
      onToggleForceSlides(false);
      if (!onPresent) navigate("/present");
      return;
    }
    setBusy(target);
    setNavErr(null);
    try {
      onToggleForceSlides(false);
      const p = await api.phase.set(state.auth.token, target, durationSec);
      dispatch({ type: "phase/set", phase: p });
      // Operator-Workflow: Workshop-Item-Klicks bringen den Operator auf
      // /present, wo das PresenterWorkshopOverlay alle Phase-Controls
      // (Timer starten, Sieger, Einreichung schließen, etc.) hat.
      if (!onPresent) navigate("/present");
    } catch (e) {
      // Vorher landete der Fehler nirgends: try/finally ohne catch. Ein
      // fehlgeschlagener Phasenwechsel sah vor Publikum genauso aus wie ein
      // geglückter — nämlich nach nichts. Wer glaubt, er habe umgeschaltet,
      // moderiert gegen einen Zustand, den es nicht gibt.
      console.error(e);
      setNavErr("Umschalten fehlgeschlagen — nochmal versuchen.");
    } finally {
      setBusy(null);
    }
  };

  const goToSlides = () => {
    onToggleForceSlides(true);
  };

  /**
   * Übergang von der Einführung in den Ablauf.
   *
   * Setzt die Phase auf „Warten auf Gäste" und bringt Adresse, PIN und QR-Code
   * auf die Leinwand — dasselbe, was der erste Schritt der Moderationsleiste
   * tut. Von hier an führt sie.
   */
  const onSitzungStarten = async () => {
    setBusy("waiting");
    setNavErr(null);
    try {
      const p = await api.phase.set(state.auth.token, "waiting");
      dispatch({ type: "phase/set", phase: p });
      if (config.waitingSlide !== undefined) {
        onToggleForceSlides(true);
        onJumpSlide(config.waitingSlide);
      }
      if (!onPresent) navigate("/present");
    } catch (e) {
      console.error(e);
      setNavErr("Sitzung konnte nicht gestartet werden — nochmal versuchen.");
    } finally {
      setBusy(null);
    }
  };

  const onJumpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const n = Number(jumpTo);
    if (Number.isFinite(n) && n >= 1 && n <= letzteFreieFolie + 1) {
      onJumpSlide(n - 1);
      onToggleForceSlides(true);
    }
  };

  const slidesActive = !workshopActive;

  /**
   * Bis wohin die Seitenleiste blättern darf.
   *
   * Bei Solo-Offsites nur die Einführung: alles danach führt die
   * Moderationsleiste. Vorher konnte man auf eine Folie blättern, die zu keinem
   * Ablauf-Schritt gehörte — dann sprang die Leiste nicht mit, und der Timer war
   * nicht erreichbar, weil dazu erst der passende Schritt aktiv sein muss.
   */
  const letzteFreieFolie =
    config.introSlides !== undefined
      ? Math.min(config.introSlides, slideTotal) - 1
      : slideTotal - 1;
  const amEndeDerEinfuehrung =
    config.introSlides !== undefined && slideIndex >= letzteFreieFolie;

  return (
    <aside className={`sidebar${collapsed ? " collapsed" : ""}`}>
      <div className="sidebar-head">
        <div className="sidebar-logo" aria-hidden="true">
          SVO
        </div>
        <div className="sidebar-title">
          {config.displayName}
          <small>Presenter</small>
        </div>
        <button
          type="button"
          className="sidebar-toggle"
          onClick={toggle}
          aria-label={collapsed ? "Navigation öffnen" : "Navigation einklappen"}
          title={collapsed ? "Aufklappen" : "Einklappen"}
        >
          {collapsed ? <IconChevronRight size={16} /> : <IconChevronLeft size={16} />}
        </button>
      </div>

      <nav className="nav-list" aria-label="Workshop-Navigation">
        {/* Reviewer-Magic-Link sessions can navigate slides + read the
         *  workshop state, but must not see Reset / Workshop-trigger
         *  controls — they can't interact with them anyway. */}
        {/* Solo-Offsites (AR): eigene Moderator-Sektion ganz oben — sie ist
         *  während der 25 Minuten die einzige Steuerung, die er anfasst. */}
        {!readOnly && config.soloTeams && (
          <SidebarSection id="ar-session" title="Sitzungssteuerung" collapsed={collapsed}>
            <ArSessionControl
              collapsed={collapsed}
              onToggleForceSlides={onToggleForceSlides}
            />
          </SidebarSection>
        )}

        {!readOnly && (
          <SidebarSection id="session" title="Workshop-Session" collapsed={collapsed}>
            <ResetWorkshopButton collapsed={collapsed} />
          </SidebarSection>
        )}

        <SidebarSection id="folien" title="Folien" collapsed={collapsed}>
          <button
            type="button"
            className="nav-item nav-item-home"
            onClick={() => {
              onJumpSlide(0);
              onToggleForceSlides(true);
            }}
            title="Zur Titel-Folie springen"
          >
            <span className="nav-icon">
              <IconHome />
            </span>
            <span className="nav-text">
              <span className="nav-num">HOME</span>
              <span className="nav-label">Titelfolie</span>
              <span className="nav-sub">Zurück zum Workshop-Start</span>
            </span>
          </button>

          <button
            type="button"
            className={`nav-item${slidesActive ? " active" : ""}`}
            onClick={goToSlides}
          >
            <span className="nav-icon">
              <IconSlides />
            </span>
            <span className="nav-text">
              <span className="nav-num">SLIDES</span>
              <span className="nav-label">Präsentation</span>
              <span className="nav-sub">
                {slideIndex + 1} von {letzteFreieFolie + 1}
              </span>
            </span>
          </button>

          <div className="sidebar-arrow-row">
            <button
              type="button"
              onClick={() => {
                onJumpSlide(Math.max(0, slideIndex - 1));
                onToggleForceSlides(true);
              }}
              disabled={slideIndex === 0}
            >
              ← Zurück
            </button>
            <button
              type="button"
              onClick={() => {
                onJumpSlide(Math.min(letzteFreieFolie, slideIndex + 1));
                onToggleForceSlides(true);
              }}
              disabled={slideIndex >= letzteFreieFolie}
            >
              Weiter →
            </button>
          </div>

          {/* Übergang von der Einführung in den Ablauf. Ein eigener Knopf, weil
           *  hier zwei Dinge passieren: die Leinwand springt auf den QR-Code,
           *  und die Sitzung steht ab jetzt beim ersten Schritt der Leiste. */}
          {amEndeDerEinfuehrung && !collapsed && (
            <button
              type="button"
              className="nav-item nav-item-uebergang"
              onClick={onSitzungStarten}
              disabled={readOnly}
              data-testid="sidebar-sitzung-starten"
              title={
                readOnly
                  ? READ_ONLY_TITLE
                  : "Zeigt Adresse, PIN und QR-Code — ab hier führt die Leiste unten"
              }
            >
              <span className="nav-icon" aria-hidden="true">
                ▸
              </span>
              <span className="nav-text">
                <span className="nav-label">Sitzung starten</span>
                <span className="nav-sub">Warten auf Gäste — QR-Code auf die Leinwand</span>
              </span>
            </button>
          )}

          {config.introSlides === undefined && (
            <form className="sidebar-jump-form" onSubmit={onJumpSubmit}>
              <input
                value={jumpTo}
                onChange={(e) => setJumpTo(e.target.value)}
                inputMode="numeric"
                aria-label="Folien-Nummer"
                placeholder="z.B. 14"
              />
              <button type="submit">Springen</button>
            </form>
          )}
        </SidebarSection>

        {/* Solo-Offsites steuern den Ablauf über die Moderationsleiste in der
         *  Inhaltsfläche. Diese Punkte täten dasselbe — „Prompten" und „KI
         *  Umsetzung" setzen dieselben Phasen wie die Schritte 3 und 5 dort —
         *  und zwei Navigationen für einen Ablauf sind eine zu viel. */}
        {!config.soloTeams && (
        <SidebarSection
          id="workshop"
          title={readOnly ? "Workshop (read-only)" : "Workshop"}
          collapsed={collapsed}
        >
        {workshopItems.map((item, idx) => {
          const matches =
            phase === item.phase ||
            (item.alsoActiveFor?.includes(phase) ?? false);
          const active = workshopActive && matches;
          const isBusy = busy === item.phase;
          const num = String(idx + 1).padStart(2, "0");
          // Special-case the "KI Umsetzung" item: replace static hint with a
          // live progress sub-line when overnight is running or reveal is up.
          const isOvernightItem = item.phase === "reveal";
          let subText: string | null = item.hint ?? null;
          let pulse = false;
          if (isOvernightItem && overnightStatus) {
            const { done, total, status } = overnightStatus;
            if (phase === "overnight" && status !== "complete") {
              subText = `läuft · ${done}/${total || "…"}`;
              pulse = true;
            } else if (phase === "reveal" || status === "complete") {
              subText = `✓ ${done} fertig`;
            }
          }
          return (
            <button
              key={item.phase}
              type="button"
              className={`nav-item${active ? " active" : ""}${isBusy ? " is-busy" : ""}${readOnly ? " is-readonly" : ""}`}
              onClick={() => goToWorkshop(item.phase, item.durationSec)}
              disabled={isBusy || readOnly}
              title={readOnly ? READ_ONLY_TITLE : undefined}
            >
              <span className="nav-icon">
                <item.Icon />
                {pulse && <span className="nav-pulse-dot" aria-hidden="true" />}
              </span>
              <span className="nav-text">
                <span className="nav-num">{num}</span>
                <span className="nav-label">{item.label}</span>
                {subText && <span className="nav-sub">{subText}</span>}
              </span>
            </button>
          );
        })}

        {navErr && !collapsed && (
          <p className="sidebar-hint is-error" role="alert">
            {navErr}
          </p>
        )}

        {forceSlides && workshopActive === false && phase !== "waiting" && !collapsed && (
          <p className="sidebar-hint">
            Workshop läuft im Hintergrund weiter — klicke einen Punkt um zur
            Workshop-Ansicht zurückzukehren.
          </p>
        )}
        </SidebarSection>
        )}

        <SidebarSection id="backend" title="Backend" collapsed={collapsed}>
          <a href="/admin" className="nav-item" style={{ textDecoration: "none" }}>
            <span className="nav-icon">
              <IconBackend />
            </span>
            <span className="nav-text">
              <span className="nav-num">ADMIN</span>
              <span className="nav-label">Backend</span>
              <span className="nav-sub">Themen · Timer · KI-Status</span>
            </span>
          </a>

          <a
            href="/showcase"
            className="nav-item"
            style={{ textDecoration: "none" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="nav-icon">
              <IconAiBuild />
            </span>
            <span className="nav-text">
              <span className="nav-num">SHOW</span>
              <span className="nav-label">Showcase</span>
              <span className="nav-sub">Carousel aller Team-Ergebnisse</span>
            </span>
          </a>
        </SidebarSection>

        <SidebarSection
          id="external"
          title="Externe Links"
          collapsed={collapsed}
          defaultOpen={false}
        >
          <a
            href="https://dev.svoos.de"
            className="nav-item"
            style={{ textDecoration: "none" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="nav-icon">
              <IconBackend />
            </span>
            <span className="nav-text">
              <span className="nav-num">SVO OS</span>
              <span className="nav-label">Plattform (dev)</span>
              <span className="nav-sub">dev.svoos.de · neuer Tab</span>
            </span>
          </a>
        </SidebarSection>
      </nav>

      <div className="sidebar-foot">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span className="live-dot" aria-hidden="true" />
          {!collapsed && <span>Celle · Uelzen</span>}
        </span>
        {!collapsed && <span>← → · F · C</span>}
        {!collapsed && <LogoutButton variant="dark" />}
      </div>
    </aside>
  );
}

/**
 * Collapsible sidebar section with header that toggles visibility.
 * Persists open/closed state in localStorage per id. When the sidebar
 * itself is collapsed (icon-rail), the header hides and children are
 * always rendered as icons.
 */
function SidebarSection({
  id,
  title,
  collapsed,
  defaultOpen = true,
  children,
}: {
  id: string;
  title: string;
  collapsed: boolean;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const storageKey = `offsite:sidebar-section-${id}`;
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return defaultOpen;
    try {
      const v = window.localStorage.getItem(storageKey);
      if (v === "1") return true;
      if (v === "0") return false;
    } catch {
      /* noop */
    }
    return defaultOpen;
  });

  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      try {
        window.localStorage.setItem(storageKey, next ? "1" : "0");
      } catch {
        /* noop */
      }
      return next;
    });
  };

  // In icon-rail mode (sidebar collapsed): always show children, no header.
  if (collapsed) {
    return <div className="sidebar-section">{children}</div>;
  }

  return (
    <div className={`sidebar-section${open ? "" : " is-closed"}`}>
      <button
        type="button"
        className="sidebar-section-toggle"
        onClick={toggle}
        aria-expanded={open}
      >
        <span className="caret" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
        {title}
      </button>
      {open && <div className="sidebar-section-body">{children}</div>}
    </div>
  );
}

function ResetWorkshopButton({ collapsed }: { collapsed: boolean }) {
  const { state, dispatch } = useStore();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [label, setLabel] = useState("");

  const open = () => {
    setLabel("");
    setConfirmOpen(true);
  };

  const doReset = async () => {
    setBusy(true);
    setMsg(null);
    setConfirmOpen(false);
    try {
      await api.admin.reset(state.auth.token, "all", label.trim() || undefined);
      // Phase wurde serverseitig auf "waiting" gesetzt — lokal spiegeln.
      const p = await api.phase.get(state.auth.token);
      dispatch({ type: "phase/set", phase: p });
      setMsg("Archiviert. Lade die Seite neu für eine frische Session.");
    } catch (err) {
      console.error(err);
      setMsg("Fehler beim Reset.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="nav-item nav-item-reset"
        onClick={open}
        disabled={busy}
        title="Kompletter Workshop-Reset (alles archivieren)"
      >
        <span className="nav-icon" aria-hidden="true">
          ↻
        </span>
        <span className="nav-text">
          <span className="nav-num">RESET</span>
          <span className="nav-label">
            {busy ? "Archiviere…" : "Neuer Workshop"}
          </span>
          <span className="nav-sub">Alles archivieren & von vorn</span>
        </span>
      </button>
      {!collapsed && msg && (
        <p className="sidebar-hint" role="status">
          {msg}
        </p>
      )}
      <ConfirmDialog
        open={confirmOpen}
        title="Kompletter Workshop-Reset?"
        message={
          "Themen, Votes, Teams, Anforderungen, Bewertungen, Outputs und das " +
          "Rahmenthema werden im Archiv abgelegt. Danach startet eine frische " +
          "Session."
        }
        confirmLabel="Ja, alles archivieren"
        cancelLabel="Abbrechen"
        variant="danger"
        onConfirm={doReset}
        onCancel={() => setConfirmOpen(false)}
        busy={busy}
        input={{
          label: "Label für das Archiv (optional)",
          value: label,
          onChange: setLabel,
          placeholder: "z.B. Probelauf 14:30",
          hint: "Hilft später im Archiv-Tab den Workshop wiederzufinden.",
        }}
      />
    </>
  );
}
